// Supabase client для прямой работы со Storage
import { createClient } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { projectId, publicAnonKey } from '../../utils/supabase/info';

const supabaseUrl = `https://${projectId}.supabase.co`;

/**
 * Инструментированный fetch — ловим редкий "No API key" на /auth/v1/token.
 * Доказано чтением auth-js: при наличии global.headers.apikey запрос НЕ может
 * уйти без apikey. Значит причина одна из:
 *   (a) apikey отсутствует уже на отправке (старый бандл/конфиг/рантайм);
 *   (b) apikey срезан в транзите (Telegram webview/прокси) — мы шлём, шлюз не видит.
 * Обёртка различает (a)/(b) и репортит в Sentry. Guard дописывает apikey при
 * пропаже — это и митигирует (a), и служит индикатором (фейлы исчезли → было (a)).
 */
const instrumentedFetch: typeof fetch = async (input, init) => {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const isSupabase = typeof url === 'string' && url.includes('.supabase.co');

  let opts = init;
  if (isSupabase) {
    const headers = new Headers(init?.headers);
    if (!headers.has('apikey')) {
      Sentry.captureMessage('supabase: apikey MISSING at send (case A)', {
        level: 'error',
        extra: { url, method: init?.method ?? 'GET', hasAuthorization: headers.has('authorization') },
      });
      headers.set('apikey', publicAnonKey); // guard + индикатор
      opts = { ...init, headers };
    }
  }

  const res = await fetch(input, opts);

  if (isSupabase && res.status === 401 && typeof url === 'string' && url.includes('/auth/v1/token')) {
    try {
      const body = await res.clone().text();
      if (body.includes('No API key') || body.includes('apikey')) {
        Sentry.captureMessage('supabase: apikey sent but gateway rejected (case B: stripped in transit)', {
          level: 'error',
          extra: { url, body: body.slice(0, 300) },
        });
      }
    } catch { /* clone/read fail — игнор */ }
  }

  return res;
};

export const supabase = createClient(supabaseUrl, publicAnonKey, {
  global: {
    fetch: instrumentedFetch,
    headers: { apikey: publicAnonKey },
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Bypass navigator.locks mechanism to prevent 5s deadlocks on page reload or strict mode mounts
    lock: async (_name, _acquireTimeout, fn) => fn(),
  },
});
